import React, { useEffect, useLayoutEffect, useMemo, useRef } from 'react'
import ReactECharts, { type EChartsInstance } from 'echarts-for-react'
import type { EChartsOption } from 'echarts'
import { cn } from '../../../lib/utils'
import { registerECharts } from './registerECharts'
import { applyAnalyticsFont, prefersReducedMotion } from '../theme/echartsTheme'

type Props = {
 option: EChartsOption
 height?: number | string
 className?: string
 loading?: boolean
 empty?: boolean
 emptyMessage?: string
 themeKey?: string
 onEvents?: Record<string, (params: unknown) => void>
}

registerECharts()

const EChartsCanvas: React.FC<Props> = ({
 option,
 height = 280,
 className,
 loading = false,
 empty = false,
 emptyMessage = 'No data',
 themeKey = 'default',
 onEvents,
}) => {
 const reducedMotion = useMemo(() => prefersReducedMotion(), [])
 const wrapperRef = useRef<HTMLDivElement | null>(null)
 const chartRef = useRef<ReactECharts | null>(null)

 const mergedOption = useMemo<EChartsOption>(
  () =>
   applyAnalyticsFont({
    ...option,
    animation: !reducedMotion && option.animation !== false,
    animationDuration: reducedMotion ? 0 : (option.animationDuration ?? 600),
    animationEasing: option.animationEasing ?? 'cubicOut',
   }),
  [option, reducedMotion],
 )

 useEffect(() => {
  registerECharts()
 }, [])

 useLayoutEffect(() => {
  const wrapper = wrapperRef.current
  if (!wrapper) return

  let frame = 0
  const resize = () => {
   if (frame) window.cancelAnimationFrame(frame)
   frame = window.requestAnimationFrame(() => {
    const instance: EChartsInstance | undefined = chartRef.current?.getEchartsInstance()
    instance?.resize()
   })
  }

  const observer = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(resize) : null
  observer?.observe(wrapper)
  window.addEventListener('resize', resize)
  resize()

  return () => {
   if (frame) window.cancelAnimationFrame(frame)
   observer?.disconnect()
   window.removeEventListener('resize', resize)
  }
 }, [height, mergedOption, themeKey])

 if (empty) {
  return (
   <div
    className={cn('flex w-full items-center justify-center text-sm text-muted-foreground', className)}
    style={{ height: typeof height === 'number' ? height : undefined }}
   >
    {emptyMessage}
   </div>
  )
 }

 return (
   <div ref={wrapperRef} className={cn('block w-full min-w-0 overflow-hidden', className)} style={{ height }}>
    <ReactECharts
     ref={chartRef}
     key={themeKey}
     className="block h-full w-full"
     style={{ height: '100%', width: '100%' }}
     option={mergedOption}
     notMerge
     lazyUpdate
     showLoading={loading}
     opts={{ renderer: 'canvas' }}
     onEvents={onEvents}
    />
   </div>
 )
}

export default EChartsCanvas
